import { useState, useEffect, useRef } from "react";
import { getApiUrl } from "@/utils/api";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ColorPickerInput } from "@/components/ui/ColorPickerInput";
import HeadingLevelSelect from "@/components/ui/HeadingLevelSelect";
import { resolveFontSize, DEFAULT_HEADING_LEVEL } from "@/utils/headingSize";
import PropertyArticleConfigFields from "./PropertyArticleConfigFields";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, Plus, Pencil, Trash2, Package, Image, Mail, Sparkles, Grid3x3, FileText, LayoutDashboard, Landmark, FileCode2, Gavel } from "lucide-react";

const API = getApiUrl();

const MODULE_TYPES = [
  { value: "header", label: "Header", icon: Grid3x3, description: "Site header with logo and branding" },
  { value: "navigation", label: "Navigation", icon: Grid3x3, description: "Site navigation with menu items and CTA" },
  { value: "footer", label: "Footer", icon: Package, description: "Footer with links and copyright" },
  { value: "text", label: "Text/HTML Content", icon: FileText, description: "Rich text or HTML content block" },
  { value: "hero", label: "Hero Banner", icon: Sparkles, description: "Large banner with CTA" },
  { value: "all_properties", label: "All Properties", icon: Grid3x3, description: "Display all enabled properties" },
  { value: "featured_properties", label: "Featured Properties", icon: Package, description: "Display featured properties" },
  { value: "property_gallery", label: "Property Image Gallery", icon: Image, description: "Manage and display the image gallery of a specific property" },
  { value: "property_article", label: "Property Article", icon: FileText, description: "AI-generated blog articles grounded in your latest listings" },
  { value: "card", label: "Card", icon: Package, description: "25%-image / 75%-content card. Manual fields or auto-list properties via URL filters." },
  { value: "go_to", label: "Go To", icon: Grid3x3, description: "Up to four CTAs to navigate between pages" },
  { value: "products", label: "Products / Shop", icon: Package, description: "Grid of live products from the catalogue (with Add to basket)" },
  { value: "basket", label: "Basket", icon: Package, description: "Inline basket contents with remove + Checkout CTA" },
  { value: "checkout", label: "Checkout", icon: Package, description: "Customer details form + Stripe payment redirect" },
  { value: "verify_photo", label: "Verify Photo", icon: Sparkles, description: "Upload two photos and get an AI-driven likeness score" },
  { value: "milestones", label: "Property Milestones", icon: LayoutDashboard, description: "Agent-only dashboard to track per-property milestones and progress" },
  { value: "house_prices_tool", label: "House Prices", icon: Landmark, description: "Public postcode → AI price estimate tool + a CTA to any page" },
  { value: "planning_applications_tool", label: "Planning Applications", icon: FileCode2, description: "Public postcode search of live UK planning applications (PlanIt) + a CTA to any page" },
  { value: "planning_appeals_tool", label: "Planning Appeals", icon: Gavel, description: "Public postcode/street search of UK Planning Inspectorate appeal decisions + a CTA to any page" },
];

export default function ModuleLibrary() {
  const [loading, setLoading] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [selectedModuleType, setSelectedModuleType] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [allPages, setAllPages] = useState({ modular: [], preset: [], custom: [] });
  
  // Config fields for the selected module type
  const [configFields, setConfigFields] = useState({});
  
  // Field management for dynamic configuration
  const [showFieldManager, setShowFieldManager] = useState(false);
  const [customFields, setCustomFields] = useState({});

  // Auto-save state — "idle" | "dirty" | "saving" | "saved" | "error"
  const [autosaveState, setAutosaveState] = useState("idle");
  const autosaveTimer = useRef(null);
  const isInitialLoad = useRef(false);

  useEffect(() => {
    if (selectedModuleType) {
      loadModuleTypeConfig(selectedModuleType);
      if (selectedModuleType === "go_to") {
        fetchAvailablePages();
      }
    }
  }, [selectedModuleType]);

  const fetchAvailablePages = async () => {
    try {
      const [modularRes, presetRes, customRes] = await Promise.all([
        axios.get(`${API}/admin/page-compositions`, { withCredentials: true }),
        axios.get(`${API}/navigation`, { withCredentials: true }),
        axios.get(`${API}/admin/custom-static-pages`, { withCredentials: true })
      ]);
      setAllPages({
        modular: modularRes.data.compositions || [],
        preset: (presetRes.data || []).filter(p => p.path && p.path !== '#'),
        custom: customRes.data.pages || []
      });
    } catch (error) {
      console.error("Failed to fetch pages:", error);
    }
  };

  const handleImageUpload = async (e, fieldName) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/admin/upload?skip_watermark=true`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.url) {
        setConfigFields(prev => ({ ...prev, [fieldName]: response.data.url }));
        toast.success("Image uploaded successfully");
      }
    } catch (error) {
      console.error("Upload failed:", error);
      toast.error("Failed to upload image");
    } finally {
      setUploading(false);
    }
  };

  const loadModuleTypeConfig = async (type) => {
    try {
      setLoading(true);
      isInitialLoad.current = true;
      // Try to load saved configuration for this module type
      const response = await axios.get(`${API}/admin/module-type-config/${type}`, {
        withCredentials: true
      });
      
      const savedConfig = response.data.config || {};
      initializeConfigFields(type, savedConfig);
      setAutosaveState("idle");
    } catch (error) {
      // If no saved config, initialize with defaults
      initializeConfigFields(type, {});
      setAutosaveState("idle");
    } finally {
      setLoading(false);
      setTimeout(() => { isInitialLoad.current = false; }, 50);
    }
  };

  // Debounced auto-save: any change to configFields for the open module type
  // triggers a save 800ms after the last edit.
  useEffect(() => {
    if (!selectedModuleType) return;
    if (isInitialLoad.current) return;
    setAutosaveState("dirty");
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(async () => {
      try {
        setAutosaveState("saving");
        await axios.put(
          `${API}/admin/module-type-config/${selectedModuleType}`,
          { config: configFields },
          { withCredentials: true }
        );
        setAutosaveState("saved");
      } catch (error) {
        setAutosaveState("error");
        toast.error(error.response?.data?.detail || "Auto-save failed");
      }
    }, 800);
    return () => {
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    };
  }, [configFields, selectedModuleType]);

  const initializeConfigFields = (type, existingConfig) => {
    switch(type) {
      case "hero":
        setConfigFields({
          title: existingConfig.title || "",
          title_color: existingConfig.title_color || "#ffffff",
          subtitle: existingConfig.subtitle || "",
          subtitle_color: existingConfig.subtitle_color || "#ffffff",
          background_image: existingConfig.background_image || "",
          cta_text: existingConfig.cta_text || "",
          cta_link: existingConfig.cta_link || "",
          cta_font_family: existingConfig.cta_font_family || "inherit",
          cta_font_size: existingConfig.cta_font_size || DEFAULT_HEADING_LEVEL,
          cta_font_weight: existingConfig.cta_font_weight || "500",
          cta_font_style: existingConfig.cta_font_style || "normal",
          cta_text_transform: existingConfig.cta_text_transform || "none",
          cta_letter_spacing: existingConfig.cta_letter_spacing || 0,
          cta_bg_color: existingConfig.cta_bg_color || "#ffffff",
          cta_text_color: existingConfig.cta_text_color || "#2f4f4f",
          cta_hover_bg_color: existingConfig.cta_hover_bg_color || "#f3f4f6",
          cta_hover_text_color: existingConfig.cta_hover_text_color || "#2f4f4f",
          cta_border_color: existingConfig.cta_border_color || "transparent",
          cta_border_width: existingConfig.cta_border_width || 0,
          cta_border_radius: existingConfig.cta_border_radius || 8,
          banner_height: existingConfig.banner_height || 500,
          image_zoom: existingConfig.image_zoom || 100,
          image_rotate: existingConfig.image_rotate || 0,
          image_opacity: existingConfig.image_opacity || 100,
          image_contrast: existingConfig.image_contrast || 100,
          image_brightness: existingConfig.image_brightness || 100
        });
        break;
      case "text":
        setConfigFields({
          content: existingConfig.content || ""
        });
        break;
      case "image_gallery":
        setConfigFields({
          images: existingConfig.images || []
        });
        break;
      case "contact_form":
        setConfigFields({
          form_fields: existingConfig.form_fields || ["name", "email", "message"]
        });
        break;
      case "header":
        setConfigFields({
          logo_url: existingConfig.logo_url || "",
          logo_text: existingConfig.logo_text || "Sell Local Homes",
          logo_font_size: existingConfig.logo_font_size || DEFAULT_HEADING_LEVEL,
          cta_text: existingConfig.cta_text || "Request Viewing",
          cta_link: existingConfig.cta_link || "/contact",
          cta_bg_color: existingConfig.cta_bg_color || "#2f4f4f",
          cta_text_color: existingConfig.cta_text_color || "#ffffff",
          show_login: existingConfig.show_login !== undefined ? existingConfig.show_login : false,
          login_link: existingConfig.login_link || "/admin/login",
        });
        break;
      case "navigation":
        setConfigFields({
          strap_line: existingConfig.strap_line || "",
          strap_line_font_size: existingConfig.strap_line_font_size || DEFAULT_HEADING_LEVEL,
          font_size: existingConfig.font_size || DEFAULT_HEADING_LEVEL,
          cta_enabled: existingConfig.cta_enabled !== false,
          cta_text: existingConfig.cta_text || "Request Viewing",
          cta_link: existingConfig.cta_link || "/contact",
          show_login: existingConfig.show_login || false,
          enable_dropdown: existingConfig.enable_dropdown !== false,
        });
        break;
      case "footer":
        setConfigFields({
          company_name: existingConfig.company_name || "SellLocalHomes",
          tagline: existingConfig.tagline || "Your trusted partner in selling your home.",
          copyright_year: existingConfig.copyright_year || new Date().getFullYear()
        });
        break;
      case "go_to":
        setConfigFields({
          font_size: existingConfig.font_size || DEFAULT_HEADING_LEVEL,
          cta_size: existingConfig.cta_size || "md",
          cta_placement: existingConfig.cta_placement || "horizontal",
          cta1_title: existingConfig.cta1_title || "",
          cta1_description: existingConfig.cta1_description || "",
          cta1_page_slug: existingConfig.cta1_page_slug !== undefined ? existingConfig.cta1_page_slug : "",
          cta2_title: existingConfig.cta2_title || "",
          cta2_description: existingConfig.cta2_description || "",
          cta2_page_slug: existingConfig.cta2_page_slug !== undefined ? existingConfig.cta2_page_slug : "",
          cta3_title: existingConfig.cta3_title || "",
          cta3_description: existingConfig.cta3_description || "",
          cta3_page_slug: existingConfig.cta3_page_slug !== undefined ? existingConfig.cta3_page_slug : "",
          cta4_title: existingConfig.cta4_title || "",
          cta4_description: existingConfig.cta4_description || "",
          cta4_page_slug: existingConfig.cta4_page_slug !== undefined ? existingConfig.cta4_page_slug : "",
        });
        break;
      default:
        setConfigFields({});
    }
  };

  const handleConfigureModuleType = (moduleType) => {
    setSelectedModuleType(moduleType);
    setShowDialog(true);
  };

  const handleSaveConfig = async () => {
    try {
      setSaving(true);
      // Cancel any pending auto-save timer to avoid duplicate PUTs
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
      // Save configuration for this module type
      await axios.put(
        `${API}/admin/module-type-config/${selectedModuleType}`,
        { config: configFields },
        { withCredentials: true }
      );
      
      toast.success("Configuration saved successfully");
      setShowDialog(false);
    } catch (error) {
      console.error("Failed to save configuration:", error);
      toast.error(error.response?.data?.detail || "Failed to save configuration");
    } finally {
      setSaving(false);
    }
  };

  // Flush any pending auto-save immediately (used before closing the dialog).
  const flushAutoSave = async () => {
    if (!selectedModuleType) return;
    if (autosaveTimer.current) {
      clearTimeout(autosaveTimer.current);
      autosaveTimer.current = null;
    }
    if (autosaveState !== "dirty" && autosaveState !== "saving") return;
    try {
      setAutosaveState("saving");
      await axios.put(
        `${API}/admin/module-type-config/${selectedModuleType}`,
        { config: configFields },
        { withCredentials: true }
      );
      setAutosaveState("saved");
    } catch (error) {
      setAutosaveState("error");
      toast.error(error.response?.data?.detail || "Auto-save failed");
    }
  };

  const closeConfigDialog = async () => {
    await flushAutoSave();
    setShowDialog(false);
  };

  const getModuleTypeInfo = (type) => {
    return MODULE_TYPES.find(t => t.value === type) || MODULE_TYPES[0];
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Module Library</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Configure module types for your pages
          </p>
        </div>
      </div>

      {/* Module Type Grid */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Module Types</CardTitle>
          <CardDescription>Configure default settings for each module type</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {MODULE_TYPES.map((type) => {
              const Icon = type.icon;
              const hasConfig = ["hero", "text", "navigation", "header", "footer", "go_to", "products", "property_article", "verify_photo"].includes(type.value);
              
              return (
                <div
                  key={type.value}
                  className={`flex items-start gap-3 p-4 border rounded-lg transition-all ${
                    hasConfig ? "hover:border-primary hover:shadow-sm cursor-pointer" : ""
                  }`}
                  onClick={() => hasConfig && handleConfigureModuleType(type.value)}
                >
                  <Icon className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-medium text-sm">{type.label}</p>
                      {hasConfig && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleConfigureModuleType(type.value);
                          }}
                        >
                          <Pencil className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{type.description}</p>
                    {hasConfig && (
                      <p className="text-xs text-primary mt-2">Click to configure</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Configure Module Type Dialog */}
      <Dialog open={showDialog} onOpenChange={(open) => { if (!open) closeConfigDialog(); else setShowDialog(true); }}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto" data-public-site>
          <DialogHeader>
            <DialogTitle>
              Configure {getModuleTypeInfo(selectedModuleType)?.label}
            </DialogTitle>
            <DialogDescription className="flex items-center justify-between gap-3">
              <span>Set default configuration for this module type</span>
              <span className="text-xs flex items-center gap-1.5 shrink-0" data-testid="autosave-indicator-modulelib">
                {autosaveState === "saving" && (<><Loader2 className="h-3 w-3 animate-spin" /> Auto-saving…</>)}
                {autosaveState === "dirty" && (<><span className="inline-block h-2 w-2 rounded-full bg-amber-500" /> Unsaved</>)}
                {autosaveState === "saved" && (<><span className="inline-block h-2 w-2 rounded-full bg-emerald-500" /> Saved</>)}
                {autosaveState === "error" && (<><span className="inline-block h-2 w-2 rounded-full bg-red-500" /> Auto-save failed</>)}
              </span>
            </DialogDescription>
          </DialogHeader>

            {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <div className="space-y-4 py-4">
              {/* Dynamic configuration fields based on module type */}
              {selectedModuleType === "header" && (
                <>
                  <div className="space-y-2">
                    <Label>Company Logo</Label>
                    {configFields.logo_url && (
                      <div className="relative w-32 h-32 mb-2 border rounded overflow-hidden bg-white">
                        <img 
                          src={configFields.logo_url} 
                          alt="Logo preview" 
                          className="w-full h-full object-contain p-2"
                        />
                        <Button
                          variant="destructive"
                          size="sm"
                          className="absolute top-2 right-2"
                          onClick={() => setConfigFields(prev => ({ ...prev, logo_url: "" }))}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, 'logo_url')}
                      disabled={uploading}
                    />
                    {uploading && <p className="text-sm text-muted-foreground">Uploading...</p>}
                  </div>

                  <div className="space-y-2">
                    <Label>Account Login Text</Label>
                    <Input
                      placeholder="Login"
                      value={configFields.login_text || ""}
                      onChange={(e) => setConfigFields(prev => ({ ...prev, login_text: e.target.value }))}
                    />
                    <p className="text-xs text-muted-foreground">
                      Text displayed on desktop. On mobile, shows as icon only.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label>Login Link</Label>
                    <Input
                      placeholder="/admin/login"
                      value={configFields.login_link || ""}
                      onChange={(e) => setConfigFields(prev => ({ ...prev, login_link: e.target.value }))}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Contact CTA Text</Label>
                    <Input
                      placeholder="Contact Us"
                      value={configFields.contact_text || ""}
                      onChange={(e) => setConfigFields(prev => ({ ...prev, contact_text: e.target.value }))}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Contact Link</Label>
                    <Input
                      placeholder="/contact"
                      value={configFields.contact_link || ""}
                      onChange={(e) => setConfigFields(prev => ({ ...prev, contact_link: e.target.value }))}
                    />
                  </div>
                </>
              )}

              {selectedModuleType === "hero" && (
                <>
                  <p className="text-xs text-muted-foreground bg-blue-50 border border-blue-200 rounded p-2">
                    Module Library captures content-only defaults. Style and colour
                    are configured per-instance in <strong>Module Instances</strong>
                    (defaults follow Site Styles).
                  </p>
                  <div className="space-y-2">
                    <Label>Title</Label>
                    <Input
                      placeholder="Welcome to our site"
                      value={configFields.title || ""}
                      onChange={(e) => setConfigFields(prev => ({ ...prev, title: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Subtitle</Label>
                    <Input
                      placeholder="Discover amazing content"
                      value={configFields.subtitle || ""}
                      onChange={(e) => setConfigFields(prev => ({ ...prev, subtitle: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Background Image</Label>
                    {configFields.background_image && (
                      <div className="relative w-full h-32 mb-2 border rounded overflow-hidden">
                        <img
                          src={configFields.background_image}
                          alt="Background preview"
                          className="w-full h-full object-cover"
                        />
                        <Button
                          variant="destructive"
                          size="sm"
                          className="absolute top-2 right-2"
                          onClick={() => setConfigFields(prev => ({ ...prev, background_image: "" }))}
                        >
                          Remove
                        </Button>
                      </div>
                    )}
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, 'background_image')}
                      disabled={uploading}
                    />
                    {uploading && <p className="text-sm text-muted-foreground">Uploading...</p>}
                  </div>
                  <div className="space-y-2">
                    <Label>CTA Button Text</Label>
                    <Input
                      placeholder="Get Started"
                      value={configFields.cta_text || ""}
                      onChange={(e) => setConfigFields(prev => ({ ...prev, cta_text: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>CTA Button Link</Label>
                    <Input
                      placeholder="/contact"
                      value={configFields.cta_link || ""}
                      onChange={(e) => setConfigFields(prev => ({ ...prev, cta_link: e.target.value }))}
                    />
                  </div>
                </>
              )}

              {selectedModuleType === "text" && (
                <div className="space-y-2">
                  <Label>Default HTML Content</Label>
                  <Textarea
                    placeholder="<p>Your HTML content here...</p>"
                    value={configFields.content || ""}
                    onChange={(e) => setConfigFields(prev => ({ ...prev, content: e.target.value }))}
                    className="min-h-32 font-mono text-sm"
                  />
                </div>
              )}

              {selectedModuleType === "header" && (
                <>
                  <p className="text-xs text-muted-foreground bg-blue-50 border border-blue-200 rounded p-2">
                    Module Library captures content-only defaults. Style and colour
                    are configured per-instance in <strong>Module Instances</strong>.
                  </p>
                  <div className="space-y-2">
                    <Label>Logo Image</Label>
                    {configFields.logo_url && (
                      <div className="relative w-32 h-16 mb-2 border rounded overflow-hidden">
                        <img src={configFields.logo_url} alt="Logo preview" className="w-full h-full object-contain" />
                        <Button variant="destructive" size="sm" className="absolute top-1 right-1" onClick={() => setConfigFields(prev => ({ ...prev, logo_url: "" }))}>Remove</Button>
                      </div>
                    )}
                    <Input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, 'logo_url')} disabled={uploading} />
                    {uploading && <p className="text-sm text-muted-foreground">Uploading...</p>}
                  </div>
                  <div className="space-y-2">
                    <Label>Logo Text</Label>
                    <Input placeholder="Sell Local Homes" value={configFields.logo_text || ""} onChange={(e) => setConfigFields(prev => ({ ...prev, logo_text: e.target.value }))} />
                  </div>
                  <div className="grid grid-cols-2 gap-3 pt-2 border-t">
                    <div className="space-y-2">
                      <Label>CTA Button Text</Label>
                      <Input placeholder="Request Viewing" value={configFields.cta_text || ""} onChange={(e) => setConfigFields(prev => ({ ...prev, cta_text: e.target.value }))} />
                    </div>
                    <div className="space-y-2">
                      <Label>CTA Button Link</Label>
                      <Input placeholder="/contact" value={configFields.cta_link || ""} onChange={(e) => setConfigFields(prev => ({ ...prev, cta_link: e.target.value }))} />
                    </div>
                  </div>
                </>
              )}

              {selectedModuleType === "navigation" && (
                <>
                  <p className="text-xs text-muted-foreground bg-blue-50 border border-blue-200 rounded p-2">
                    Logo image and text are configured in the <strong>Header</strong>
                    module. Nav links are managed via the <strong>Navigation</strong>
                    CMS tab. Style and colour are configured per-instance in
                    <strong> Module Instances</strong>.
                  </p>
                  <div className="space-y-2">
                    <Label>Strap Line</Label>
                    <Input placeholder="Your trusted partner in selling your home" value={configFields.strap_line || ""} onChange={(e) => setConfigFields(prev => ({ ...prev, strap_line: e.target.value }))} />
                  </div>
                  <div className="pt-2 border-t space-y-3">
                    <div className="flex items-center gap-3">
                      <input type="checkbox" id="cta_enabled" checked={configFields.cta_enabled !== false} onChange={(e) => setConfigFields(prev => ({ ...prev, cta_enabled: e.target.checked }))} />
                      <Label htmlFor="cta_enabled">Show CTA Button</Label>
                    </div>
                    {configFields.cta_enabled !== false && (
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label className="text-xs">CTA Text</Label>
                          <Input placeholder="Request Viewing" value={configFields.cta_text || ""} onChange={(e) => setConfigFields(prev => ({ ...prev, cta_text: e.target.value }))} />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs">CTA Link</Label>
                          <Input placeholder="/contact" value={configFields.cta_link || ""} onChange={(e) => setConfigFields(prev => ({ ...prev, cta_link: e.target.value }))} />
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}

              {selectedModuleType === "footer" && (
                <>
                  <p className="text-xs text-muted-foreground bg-blue-50 border border-blue-200 rounded p-2">
                    Module Library captures content-only defaults. Style and colour
                    are configured per-instance in <strong>Module Instances</strong>.
                  </p>
                  <div className="space-y-2">
                    <Label>Company Name</Label>
                    <Input
                      placeholder="SellLocalHomes"
                      value={configFields.company_name || ""}
                      onChange={(e) => setConfigFields(prev => ({ ...prev, company_name: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Tagline</Label>
                    <Input
                      placeholder="Your trusted partner"
                      value={configFields.tagline || ""}
                      onChange={(e) => setConfigFields(prev => ({ ...prev, tagline: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Copyright Year</Label>
                    <Input
                      type="number"
                      placeholder="2025"
                      value={configFields.copyright_year || ""}
                      onChange={(e) => setConfigFields(prev => ({ ...prev, copyright_year: parseInt(e.target.value) }))}
                    />
                  </div>
                </>
              )}

              {selectedModuleType === "products" && (
                <p className="text-xs text-muted-foreground bg-blue-50 border border-blue-200 rounded p-2">
                  Products / Shop is fully configured per-instance (title, layout, category
                  filters, Add to basket, etc.) in <strong>CMS → Instances</strong> — this
                  module type has no shared defaults to set here.
                </p>
              )}

              {selectedModuleType === "property_article" && (
                <PropertyArticleConfigFields
                  config={configFields}
                  onChange={setConfigFields}
                />
              )}

              {selectedModuleType === "go_to" && (
                <>
                  <p className="text-xs text-muted-foreground bg-blue-50 border border-blue-200 rounded p-2">
                    Module Library captures content-only defaults. Style and colour
                    are configured per-instance in <strong>Module Instances</strong>.
                  </p>

                  {/* Layout Controls (structural, not visual styling) */}
                  <div className="p-3 bg-slate-50 rounded-lg border space-y-3">
                    <h3 className="font-semibold text-sm">Layout</h3>
                    <div className="space-y-1">
                      <Label className="text-xs">Placement</Label>
                      <select
                        value={configFields.cta_placement || "horizontal"}
                        onChange={(e) => setConfigFields(prev => ({ ...prev, cta_placement: e.target.value }))}
                        className="w-full h-8 px-2 border border-input rounded-md bg-background text-sm"
                      >
                        <option value="horizontal">2 Columns</option>
                        <option value="grid-2">2×2 Grid</option>
                        <option value="grid-4">4 Columns</option>
                        <option value="vertical">Vertical Stack</option>
                      </select>
                    </div>
                    <p className="text-xs text-muted-foreground">CTAs without a title will be hidden on the live page.</p>
                  </div>

                  {/* Four CTA Cards */}
                  {[1, 2, 3, 4].map((n) => (
                    <div key={n} className="border rounded-lg p-3 space-y-3">
                      <h3 className="font-medium text-sm text-muted-foreground">CTA {n}</h3>
                      <div className="space-y-2">
                        <Label className="text-xs">Title <span className="text-muted-foreground">(leave blank to hide)</span></Label>
                        <Input
                          placeholder={n === 1 ? "View All Properties" : n === 2 ? "Contact Us" : `CTA ${n} Title`}
                          value={configFields[`cta${n}_title`] || ""}
                          onChange={(e) => setConfigFields(prev => ({ ...prev, [`cta${n}_title`]: e.target.value }))}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Description (Optional)</Label>
                        <Input
                          placeholder="Short description…"
                          value={configFields[`cta${n}_description`] || ""}
                          onChange={(e) => setConfigFields(prev => ({ ...prev, [`cta${n}_description`]: e.target.value }))}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Navigate To</Label>
                        <select
                          value={configFields[`cta${n}_page_slug`] || ""}
                          onChange={(e) => setConfigFields(prev => ({ ...prev, [`cta${n}_page_slug`]: e.target.value }))}
                          className="w-full h-10 px-3 py-2 border border-input rounded-md bg-background text-sm"
                        >
                          <option value="">Select a page</option>
                          {allPages.modular.length > 0 && (
                            <optgroup label="Modular Pages">
                              {allPages.modular.map((page) => (
                                <option key={page.composition_id} value={page.page_slug === null ? "/" : (page.page_slug || "/")}>
                                  {page.page_title} ({page.page_slug === null ? "/" : `/${page.page_slug}`})
                                </option>
                              ))}
                            </optgroup>
                          )}
                          {allPages.preset.length > 0 && (
                            <optgroup label="Preset Pages">
                              {allPages.preset.map((page) => (
                                <option key={page.nav_id} value={page.path}>
                                  {page.title} ({page.path})
                                </option>
                              ))}
                            </optgroup>
                          )}
                          {allPages.custom.length > 0 && (
                            <optgroup label="Custom Pages">
                              {allPages.custom.map((page) => (
                                <option key={page.page_id} value={`/${page.slug}`}>
                                  {page.title} (/{page.slug})
                                </option>
                              ))}
                            </optgroup>
                          )}
                        </select>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}

          <DialogFooter className="hidden" />
        </DialogContent>
      </Dialog>
    </div>
  );
}
